// src/Studio/engine/cameraEngine.ts
// Camera Engine (Phase 5) - Cinematic camera moves for viewport animation
// Applies pan, zoom, and rotation transforms to the entire canvas

import type {
  CameraTrack,
  CameraTransform,
  CameraKeyframe,
  CameraPresetId,
  AnimationEasing,
  CameraSettingsV1,
  Element,
} from "../types";
import { applyKeyframeAnimations } from "../canvas/utils/keyframeRenderer";
import { computeFollowTransform, type FollowState } from "./followEngine";

// ==================== Default Camera State ====================

export const DEFAULT_CAMERA_TRANSFORM: CameraTransform = {
  x: 0,
  y: 0,
  scale: 1,
  rotation: 0,
};

export function createDefaultCameraTrack(): CameraTrack {
  return {
    enabled: false,
    keyframes: [],
  };
}

// ==================== Easing Functions ====================

function ease(easing: AnimationEasing, t: number): number {
  const x = Math.max(0, Math.min(1, t));
  switch (easing) {
    case "linear":
      return x;
    case "easeIn":
      return x * x;
    case "easeOut":
      return 1 - (1 - x) * (1 - x);
    case "easeInOut":
      return x < 0.5 ? 2 * x * x : 1 - Math.pow(-2 * x + 2, 2) / 2;
    default:
      return x;
  }
}

function lerp(a: number, b: number, t: number): number {
  return a + (b - a) * t;
}

function clamp(value: number, min: number, max: number): number {
  return Math.max(min, Math.min(max, value));
}

function clamp01(value: number): number {
  return clamp(value, 0, 1);
}

export type CameraEvaluationContext = {
  canvasW: number;
  canvasH: number;
  elementsById?: Map<string, Element>;
};

export type CameraEvaluationOptions = {
  allowAdvanced?: boolean;
  context?: CameraEvaluationContext;
  smoothingOverride?: {
    method?: "linear" | "catmull-rom" | "bezier";
    tension?: number;
  };
  ignoreFollow?: boolean;
  ignoreConstraints?: boolean;
};

const cameraFollowStateCache = new WeakMap<CameraTrack, { state: FollowState; lastPlayhead: number }>();

function getSortedKeyframes(keyframes: CameraKeyframe[]): CameraKeyframe[] {
  let sorted = keyframes;
  for (let i = 1; i < sorted.length; i++) {
    if (sorted[i].time < sorted[i - 1].time) {
      sorted = [...sorted].sort((a, b) => a.time - b.time);
      break;
    }
  }
  return sorted;
}

function catmullRom(p0: number, p1: number, p2: number, p3: number, t: number, tension: number): number {
  const tt = clamp01(t);
  const s = (1 - clamp01(tension)) / 2;
  const t2 = tt * tt;
  const t3 = t2 * tt;
  const m1 = (p2 - p0) * s;
  const m2 = (p3 - p1) * s;
  const h00 = 2 * t3 - 3 * t2 + 1;
  const h10 = t3 - 2 * t2 + tt;
  const h01 = -2 * t3 + 3 * t2;
  const h11 = t3 - t2;
  return h00 * p1 + h10 * m1 + h01 * p2 + h11 * m2;
}

function cubicBezier(p0: number, p1: number, p2: number, p3: number, t: number): number {
  const tt = clamp01(t);
  const mt = 1 - tt;
  const mt2 = mt * mt;
  const tt2 = tt * tt;
  return mt2 * mt * p0 + 3 * mt2 * tt * p1 + 3 * mt * tt2 * p2 + tt2 * tt * p3;
}

function smoothValue(
  method: "linear" | "catmull-rom" | "bezier",
  p0: number,
  p1: number,
  p2: number,
  p3: number,
  t: number,
  tension: number
): number {
  if (method === "catmull-rom") {
    return catmullRom(p0, p1, p2, p3, t, tension);
  }
  if (method === "bezier") {
    const k = (1 - clamp01(tension)) / 6;
    const cp1 = p1 + (p2 - p0) * k;
    const cp2 = p2 - (p3 - p1) * k;
    return cubicBezier(p1, cp1, cp2, p2, t);
  }
  return lerp(p1, p2, clamp01(t));
}

function computePanLimits(
  canvasW: number,
  canvasH: number,
  bounds: { x: number; y: number; width: number; height: number },
  zoomScale: number
): { minX: number; maxX: number; minY: number; maxY: number } {
  const safeW = Number.isFinite(canvasW) && canvasW > 0 ? canvasW : 1920;
  const safeH = Number.isFinite(canvasH) && canvasH > 0 ? canvasH : 1080;
  const centerX = safeW / 2;
  const centerY = safeH / 2;
  const scale = Number.isFinite(zoomScale) && zoomScale > 0 ? zoomScale : 1;
  const left = bounds.x;
  const right = bounds.x + bounds.width;
  const top = bounds.y;
  const bottom = bounds.y + bounds.height;

  const panXLeft = (left - centerX) + centerX / scale;
  const panXRight = (right - centerX) - centerX / scale;
  const panYTop = (top - centerY) + centerY / scale;
  const panYBottom = (bottom - centerY) - centerY / scale;

  return {
    minX: Math.min(panXLeft, panXRight),
    maxX: Math.max(panXLeft, panXRight),
    minY: Math.min(panYBottom, panYTop),
    maxY: Math.max(panYBottom, panYTop),
  };
}

function getAnimatedElementAtTime(
  element: Element,
  time: number,
  context?: CameraEvaluationContext
): Element {
  const baseProps = {
    x: element.x,
    y: element.y,
    scaleX: element.scaleX ?? 1,
    scaleY: element.scaleY ?? 1,
    rotation: element.rotation ?? 0,
    opacity: element.opacity ?? 1,
  };
  const animated = applyKeyframeAnimations(
    element,
    time,
    baseProps,
    context?.elementsById ? { elementsById: context.elementsById } : undefined
  );

  return {
    ...element,
    x: animated.x,
    y: animated.y,
    scaleX: animated.scaleX,
    scaleY: animated.scaleY,
    rotation: animated.rotation,
    opacity: animated.opacity,
  };
}

function getElementBoundsAtTime(
  element: Element,
  time: number,
  context?: CameraEvaluationContext
): { x: number; y: number; width: number; height: number } | null {
  const animated = getAnimatedElementAtTime(element, time, context);
  const scaleX = Number.isFinite(animated.scaleX) ? Number(animated.scaleX) : 1;
  const scaleY = Number.isFinite(animated.scaleY) ? Number(animated.scaleY) : 1;
  const width = Number(animated.width) * scaleX;
  const height = Number(animated.height) * scaleY;
  if (!Number.isFinite(width) || !Number.isFinite(height) || width <= 0 || height <= 0) {
    return null;
  }
  return {
    x: Number(animated.x),
    y: Number(animated.y),
    width,
    height,
  };
}

// ==================== Camera Transform Interpolation ====================

/**
 * Get interpolated camera transform at a given time.
 */
export function getCameraTransformAtTime(
  track: CameraTrack,
  time: number
): CameraTransform {
  if (!track.enabled || track.keyframes.length === 0) {
    return { ...DEFAULT_CAMERA_TRANSFORM };
  }

  // Assume keyframes are already sorted (we keep them sorted on insert), but fall back if needed.
  let sorted = track.keyframes;
  for (let i = 1; i < sorted.length; i++) {
    if (sorted[i].time < sorted[i - 1].time) {
      sorted = [...sorted].sort((a, b) => a.time - b.time);
      break;
    }
  }

  // Before first keyframe
  if (time <= sorted[0].time) {
    return { ...sorted[0].transform };
  }

  // After last keyframe
  if (time >= sorted[sorted.length - 1].time) {
    return { ...sorted[sorted.length - 1].transform };
  }

  // Find surrounding keyframes
  let before: CameraKeyframe | null = null;
  let after: CameraKeyframe | null = null;

  for (let i = 0; i < sorted.length - 1; i++) {
    if (time >= sorted[i].time && time <= sorted[i + 1].time) {
      before = sorted[i];
      after = sorted[i + 1];
      break;
    }
  }

  if (!before || !after) {
    return { ...DEFAULT_CAMERA_TRANSFORM };
  }

  // Interpolate between keyframes
  const duration = after.time - before.time;
  const t = duration > 0 ? (time - before.time) / duration : 0;
  const easedT = ease(before.easing, t);

  return {
    x: lerp(before.transform.x, after.transform.x, easedT),
    y: lerp(before.transform.y, after.transform.y, easedT),
    scale: lerp(before.transform.scale, after.transform.scale, easedT),
    rotation: lerp(before.transform.rotation, after.transform.rotation, easedT),
  };
}

export function getCameraTransformAtTimeAdvanced(
  track: CameraTrack,
  time: number,
  opts?: CameraEvaluationOptions
): CameraTransform {
  const allowAdvanced = !!opts?.allowAdvanced;
  const settings = (track.settings ?? {}) as CameraSettingsV1;
  if (!allowAdvanced || !settings.enabled) {
    return getCameraTransformAtTime(track, time);
  }
  if (!track.enabled) {
    return { ...DEFAULT_CAMERA_TRANSFORM };
  }

  let result: CameraTransform = { ...DEFAULT_CAMERA_TRANSFORM };

  if (track.keyframes.length > 0) {
    const sorted = getSortedKeyframes(track.keyframes);

    if (time <= sorted[0].time) {
      result = { ...sorted[0].transform };
    } else if (time >= sorted[sorted.length - 1].time) {
      result = { ...sorted[sorted.length - 1].transform };
    } else {
      let before: CameraKeyframe | null = null;
      let after: CameraKeyframe | null = null;
      let beforeIndex = 0;

      for (let i = 0; i < sorted.length - 1; i++) {
        if (time >= sorted[i].time && time <= sorted[i + 1].time) {
          before = sorted[i];
          after = sorted[i + 1];
          beforeIndex = i;
          break;
        }
      }

      if (!before || !after) {
        result = { ...DEFAULT_CAMERA_TRANSFORM };
      } else {
        const duration = after.time - before.time;
        const t = duration > 0 ? (time - before.time) / duration : 0;
        const perSegment = settings.keyframeControls?.mode === "per-segment";
        const segmentEasing = perSegment
          ? (before.easingOut ?? settings.keyframeControls?.defaultEasing ?? before.easing)
          : before.easing;
        const easedT = ease(segmentEasing, t);

        if (perSegment && before.hold) {
          result = { ...before.transform };
        } else {
          const smoothingSettings = settings.pathSmoothing;
          const smoothingEnabled = !!smoothingSettings?.enabled || !!opts?.smoothingOverride?.method;
          const smoothingMethod = smoothingEnabled
            ? (opts?.smoothingOverride?.method ?? smoothingSettings?.playbackMethod ?? "linear")
            : "linear";
          const smoothingTension = clamp01(
            opts?.smoothingOverride?.tension ?? smoothingSettings?.playbackTension ?? 0.5
          );

          let x = lerp(before.transform.x, after.transform.x, easedT);
          let y = lerp(before.transform.y, after.transform.y, easedT);

          if (smoothingMethod !== "linear") {
            const p0 = sorted[Math.max(0, beforeIndex - 1)].transform;
            const p1 = before.transform;
            const p2 = after.transform;
            const p3 = sorted[Math.min(sorted.length - 1, beforeIndex + 2)].transform;
            x = smoothValue(smoothingMethod, p0.x, p1.x, p2.x, p3.x, easedT, smoothingTension);
            y = smoothValue(smoothingMethod, p0.y, p1.y, p2.y, p3.y, easedT, smoothingTension);
          }

          result = {
            x,
            y,
            scale: lerp(before.transform.scale, after.transform.scale, easedT),
            rotation: lerp(before.transform.rotation, after.transform.rotation, easedT),
          };
        }
      }
    }
  }

  const context = opts?.context;
  const applyFollow = !opts?.ignoreFollow && !!settings.follow?.enabled && !!context;
  if (applyFollow && settings.follow?.targetElementId && context?.elementsById) {
    const target = context.elementsById.get(settings.follow.targetElementId);
    if (target && context.canvasW > 0 && context.canvasH > 0) {
      const targetAnimated = getAnimatedElementAtTime(target, time, context);
      const targetScaleX = Number.isFinite(targetAnimated.scaleX) ? Number(targetAnimated.scaleX) : 1;
      const targetScaleY = Number.isFinite(targetAnimated.scaleY) ? Number(targetAnimated.scaleY) : 1;
      const targetFollowElement = {
        ...targetAnimated,
        width: Number(targetAnimated.width) * targetScaleX,
        height: Number(targetAnimated.height) * targetScaleY,
        scaleX: 1,
        scaleY: 1,
      } as Element;
      if (Number.isFinite(targetFollowElement.width) && Number.isFinite(targetFollowElement.height)) {
        const cameraElement = {
          x: result.x,
          y: result.y,
          width: context.canvasW,
          height: context.canvasH,
          rotation: result.rotation,
        } as Element;

        const cached = cameraFollowStateCache.get(track);
        const prevState =
          cached && Number.isFinite(cached.lastPlayhead) && time >= cached.lastPlayhead
            ? cached.state
            : undefined;

        const { result: followResult, state: nextState } = computeFollowTransform(
          cameraElement,
          {
            targetElementId: settings.follow.targetElementId,
            mode: settings.follow.mode ?? "position",
            offsetX: settings.follow.offsetX ?? 0,
            offsetY: settings.follow.offsetY ?? 0,
            smoothing: settings.follow.smoothing ?? 0,
          },
          targetFollowElement,
          time,
          prevState,
          settings.follow.mode === "lookAt" ? { rotationOffsetDeg: result.rotation } : undefined
        );

        cameraFollowStateCache.set(track, { state: nextState, lastPlayhead: time });

        result = {
          ...result,
          x: followResult.x ?? result.x,
          y: followResult.y ?? result.y,
          rotation: followResult.rotation ?? result.rotation,
        };
      }
    }
  }

  const applyConstraints = !opts?.ignoreConstraints && !!settings.constraints?.enabled && !!context;
  if (applyConstraints && context?.canvasW > 0 && context.canvasH > 0) {
    let bounds: { x: number; y: number; width: number; height: number } | null = null;
    const referenceId = settings.referenceElementId || settings.follow?.targetElementId;
    if (referenceId && context.elementsById) {
      const target = context.elementsById.get(referenceId);
      if (target) {
        bounds = getElementBoundsAtTime(target, time, context);
      }
    }

    if (bounds) {
      const limits = computePanLimits(context.canvasW, context.canvasH, bounds, result.scale);
      const mode = settings.constraints?.mode ?? "contain";
      const marginX = mode === "safe-margin" ? Math.max(0, settings.constraints?.marginX ?? 0) : 0;
      const marginY = mode === "safe-margin" ? Math.max(0, settings.constraints?.marginY ?? 0) : 0;
      let minX = limits.minX + marginX;
      let maxX = limits.maxX - marginX;
      let minY = limits.minY + marginY;
      let maxY = limits.maxY - marginY;

      if (minX > maxX) {
        const mid = (minX + maxX) / 2;
        minX = mid;
        maxX = mid;
      }
      if (minY > maxY) {
        const mid = (minY + maxY) / 2;
        minY = mid;
        maxY = mid;
      }

      result = {
        ...result,
        x: clamp(result.x, minX, maxX),
        y: clamp(result.y, minY, maxY),
      };
    }
  }

  return result;
}

export function sampleCameraPath(
  track: CameraTrack,
  opts?: {
    samples?: number;
    allowAdvanced?: boolean;
    context?: CameraEvaluationContext;
    smoothingOverride?: CameraEvaluationOptions["smoothingOverride"];
    ignoreFollow?: boolean;
    ignoreConstraints?: boolean;
  }
): Array<{ time: number; transform: CameraTransform }> {
  if (!track.enabled || track.keyframes.length < 2) return [];
  const sorted = getSortedKeyframes(track.keyframes);
  const start = sorted[0].time;
  const end = sorted[sorted.length - 1].time;
  const count = Math.max(2, Math.floor(opts?.samples ?? 60));
  const results: Array<{ time: number; transform: CameraTransform }> = [];

  for (let i = 0; i < count; i++) {
    const t = count === 1 ? start : start + (i / (count - 1)) * (end - start);
    const transform = (opts?.allowAdvanced ?? false)
      ? getCameraTransformAtTimeAdvanced(track, t, {
        allowAdvanced: true,
        context: opts?.context,
        smoothingOverride: opts?.smoothingOverride,
        ignoreFollow: opts?.ignoreFollow,
        ignoreConstraints: opts?.ignoreConstraints,
      })
      : getCameraTransformAtTime(track, t);
    results.push({ time: t, transform });
  }

  return results;
}

// ==================== Camera Transform Application ====================

/**
 * Convert camera transform to Konva stage transform matrix.
 * The camera transform represents the "virtual camera" position,
 * so we apply the inverse to the stage.
 */
export function cameraToStageTransform(
  camera: CameraTransform,
  canvasWidth: number,
  canvasHeight: number
): {
  x: number;
  y: number;
  scaleX: number;
  scaleY: number;
  rotation: number;
  offsetX: number;
  offsetY: number;
} {
  // Camera x/y is the offset of the virtual camera from center
  // To show this in the viewport, we translate the stage in the opposite direction
  const centerX = canvasWidth / 2;
  const centerY = canvasHeight / 2;

  return {
    x: centerX - camera.x * camera.scale,
    y: centerY - camera.y * camera.scale,
    scaleX: camera.scale,
    scaleY: camera.scale,
    rotation: -camera.rotation, // Inverse rotation
    offsetX: centerX,
    offsetY: centerY,
  };
}

/**
 * Convert screen coordinates to world coordinates accounting for camera.
 * Use this for pointer/mouse position conversion during editing.
 */
export function screenToWorld(
  screenX: number,
  screenY: number,
  camera: CameraTransform,
  canvasWidth: number,
  canvasHeight: number
): { x: number; y: number } {
  const centerX = canvasWidth / 2;
  const centerY = canvasHeight / 2;

  // Translate to center
  let x = screenX - centerX;
  let y = screenY - centerY;

  // Rotate back (inverse of camera rotation)
  const radians = (camera.rotation * Math.PI) / 180;
  const cos = Math.cos(radians);
  const sin = Math.sin(radians);
  const rx = x * cos + y * sin;
  const ry = -x * sin + y * cos;

  // Scale back
  x = rx / camera.scale;
  y = ry / camera.scale;

  // Translate back by camera position
  x += centerX + camera.x;
  y += centerY + camera.y;

  return { x, y };
}

/**
 * Convert world coordinates to screen coordinates accounting for camera.
 */
export function worldToScreen(
  worldX: number,
  worldY: number,
  camera: CameraTransform,
  canvasWidth: number,
  canvasHeight: number
): { x: number; y: number } {
  const centerX = canvasWidth / 2;
  const centerY = canvasHeight / 2;

  // Translate from camera position
  let x = worldX - centerX - camera.x;
  let y = worldY - centerY - camera.y;

  // Scale
  x *= camera.scale;
  y *= camera.scale;

  // Rotate (camera rotation)
  const radians = (-camera.rotation * Math.PI) / 180;
  const cos = Math.cos(radians);
  const sin = Math.sin(radians);
  const rx = x * cos - y * sin;
  const ry = x * sin + y * cos;

  // Translate back to center
  x = rx + centerX;
  y = ry + centerY;

  return { x, y };
}

// ==================== Camera Presets ====================

export interface CameraPreset {
  id: CameraPresetId;
  name: string;
  description: string;
  duration: number; // Default duration in seconds
  keyframes: Omit<CameraKeyframe, "time">[]; // Times will be calculated based on duration
}

export const CAMERA_PRESETS: CameraPreset[] = [
  {
    id: "slow-push-in",
    name: "Slow Push In",
    description: "Gentle zoom in toward center",
    duration: 3,
    keyframes: [
      { transform: { x: 0, y: 0, scale: 1, rotation: 0 }, easing: "easeInOut" },
      { transform: { x: 0, y: 0, scale: 1.15, rotation: 0 }, easing: "easeOut" },
    ],
  },
  {
    id: "slow-pull-out",
    name: "Slow Pull Out",
    description: "Gentle zoom out from center",
    duration: 3,
    keyframes: [
      { transform: { x: 0, y: 0, scale: 1.15, rotation: 0 }, easing: "easeInOut" },
      { transform: { x: 0, y: 0, scale: 1, rotation: 0 }, easing: "easeOut" },
    ],
  },
  {
    id: "pan-left-right",
    name: "Pan Left → Right",
    description: "Smooth horizontal pan",
    duration: 4,
    keyframes: [
      { transform: { x: -100, y: 0, scale: 1, rotation: 0 }, easing: "easeInOut" },
      { transform: { x: 100, y: 0, scale: 1, rotation: 0 }, easing: "easeOut" },
    ],
  },
  {
    id: "pan-right-left",
    name: "Pan Right → Left",
    description: "Smooth horizontal pan (reverse)",
    duration: 4,
    keyframes: [
      { transform: { x: 100, y: 0, scale: 1, rotation: 0 }, easing: "easeInOut" },
      { transform: { x: -100, y: 0, scale: 1, rotation: 0 }, easing: "easeOut" },
    ],
  },
  {
    id: "dolly-zoom",
    name: "Dolly Zoom",
    description: "Hitchcock vertigo effect (subtle)",
    duration: 3,
    keyframes: [
      { transform: { x: 0, y: 0, scale: 0.95, rotation: 0 }, easing: "easeInOut" },
      { transform: { x: 0, y: 10, scale: 1.08, rotation: 0 }, easing: "easeInOut" },
      { transform: { x: 0, y: 0, scale: 1, rotation: 0 }, easing: "easeOut" },
    ],
  },
  {
    id: "handheld-micro",
    name: "Handheld Micro",
    description: "Subtle shake for organic feel",
    duration: 2,
    keyframes: [
      { transform: { x: 0, y: 0, scale: 1, rotation: 0 }, easing: "linear" },
      { transform: { x: 2, y: -1, scale: 1.002, rotation: 0.3 }, easing: "linear" },
      { transform: { x: -1, y: 2, scale: 0.998, rotation: -0.2 }, easing: "linear" },
      { transform: { x: 1, y: -1, scale: 1.001, rotation: 0.1 }, easing: "linear" },
      { transform: { x: 0, y: 0, scale: 1, rotation: 0 }, easing: "easeOut" },
    ],
  },
  {
    id: "rotate-cw",
    name: "Rotate CW",
    description: "Slow clockwise rotation",
    duration: 4,
    keyframes: [
      { transform: { x: 0, y: 0, scale: 1, rotation: 0 }, easing: "easeInOut" },
      { transform: { x: 0, y: 0, scale: 1, rotation: 5 }, easing: "easeOut" },
    ],
  },
  {
    id: "rotate-ccw",
    name: "Rotate CCW",
    description: "Slow counter-clockwise rotation",
    duration: 4,
    keyframes: [
      { transform: { x: 0, y: 0, scale: 1, rotation: 0 }, easing: "easeInOut" },
      { transform: { x: 0, y: 0, scale: 1, rotation: -5 }, easing: "easeOut" },
    ],
  },
  {
    id: "orbit",
    name: "Orbit",
    description: "Circular camera orbit around center",
    duration: 6,
    keyframes: [
      { transform: { x: 0, y: -60, scale: 1.05, rotation: 0 }, easing: "easeInOut" },
      { transform: { x: 60, y: 0, scale: 1.05, rotation: 2 }, easing: "easeInOut" },
      { transform: { x: 0, y: 60, scale: 1.05, rotation: 0 }, easing: "easeInOut" },
      { transform: { x: -60, y: 0, scale: 1.05, rotation: -2 }, easing: "easeInOut" },
      { transform: { x: 0, y: -60, scale: 1.05, rotation: 0 }, easing: "easeOut" },
    ],
  },
  {
    id: "diagonal-pan",
    name: "Diagonal Pan",
    description: "Pan diagonally from corner to corner",
    duration: 4,
    keyframes: [
      { transform: { x: -80, y: -50, scale: 1.1, rotation: 0 }, easing: "easeInOut" },
      { transform: { x: 80, y: 50, scale: 1.1, rotation: 0 }, easing: "easeOut" },
    ],
  },
  {
    id: "dramatic-push",
    name: "Dramatic Push",
    description: "Fast dramatic zoom with slight rotation",
    duration: 2,
    keyframes: [
      { transform: { x: 0, y: 0, scale: 0.9, rotation: -2 }, easing: "easeIn" },
      { transform: { x: 0, y: -20, scale: 1.25, rotation: 0 }, easing: "easeOut" },
    ],
  },
  {
    id: "ken-burns-tl",
    name: "Ken Burns TL",
    description: "Pan from top-left with subtle zoom",
    duration: 5,
    keyframes: [
      { transform: { x: -100, y: -70, scale: 1.15, rotation: 0 }, easing: "easeInOut" },
      { transform: { x: 30, y: 20, scale: 1, rotation: 0 }, easing: "easeOut" },
    ],
  },
];

/**
 * Apply a camera preset to create a CameraTrack.
 */
export function applyCameraPreset(
  presetId: CameraPresetId,
  startTime: number = 0,
  duration?: number
): CameraTrack {
  const preset = CAMERA_PRESETS.find((p) => p.id === presetId);
  if (!preset) {
    return createDefaultCameraTrack();
  }

  const actualDuration = duration ?? preset.duration;
  const numKeyframes = preset.keyframes.length;

  const keyframes: CameraKeyframe[] = preset.keyframes.map((kf, i) => ({
    time: startTime + (i / (numKeyframes - 1)) * actualDuration,
    transform: { ...kf.transform },
    easing: kf.easing,
  }));

  return {
    enabled: true,
    keyframes,
  };
}

/**
 * Add a keyframe to camera track at current time.
 */
export function addCameraKeyframe(
  track: CameraTrack,
  time: number,
  transform: CameraTransform,
  easing: AnimationEasing = "easeInOut"
): CameraTrack {
  const existingIdx = track.keyframes.findIndex((kf) => Math.abs(kf.time - time) < 0.01);

  if (existingIdx >= 0) {
    // Update existing keyframe
    const newKeyframes = [...track.keyframes];
    newKeyframes[existingIdx] = { time, transform: { ...transform }, easing };
    return { ...track, keyframes: newKeyframes };
  }

  // Add new keyframe (keep time-sorted)
  const next = [...track.keyframes, { time, transform: { ...transform }, easing }];
  next.sort((a, b) => a.time - b.time);
  return { ...track, enabled: true, keyframes: next };
}

/**
 * Remove a keyframe at specific time.
 */
export function removeCameraKeyframe(track: CameraTrack, time: number): CameraTrack {
  return {
    ...track,
    keyframes: track.keyframes.filter((kf) => Math.abs(kf.time - time) >= 0.01),
  };
}

/**
 * Clear all camera keyframes and disable track.
 */
export function clearCameraTrack(): CameraTrack {
  return createDefaultCameraTrack();
}
